import { BundleAlertDefinition } from 'src/models/transfer/input/BundleAlertDefinition';
import { getLogger } from '../diagnostics/logger';
import moment from 'moment';
import {
  CREATE_BUNDLE_ALERT_DEFINITION_SUCCESS,
  DELETE_BUNDLE_ALERT_DEFINITION_SUCCESS,
  LOAD_BUNDLE_ALERT_DEFINITIONS_FAILURE,
  LOAD_BUNDLE_ALERT_DEFINITIONS_PENDING,
  LOAD_BUNDLE_ALERT_DEFINITIONS_SUCCESS,
  UPDATE_BUNDLE_ALERT_DEFINITION_SUCCESS,
} from '../actions/actionTypes';
import { Action, ActionMeta } from '../actions/commonActions';
import { SimManagementError } from '../models/Errors';
import { initialAppState } from '../models/state/appState';
import {
  BundleAlertDefinitionsCacheTimeout,
  BundleAlertDefinitionsState,
  getDefaultBundleAlertDefinitionState,
} from '../models/state/bundleAlertDefinitionsState';

const log = getLogger('simManagement.api.bundleAlertDefinitionsReducer');

export default function bundleAlertDefinitionsReducer(
  state: BundleAlertDefinitionsState = initialAppState.bundleAlertDefinitions,
  action: Action<any>
): BundleAlertDefinitionsState {
  switch (action.type) {
    case LOAD_BUNDLE_ALERT_DEFINITIONS_PENDING: {
      const { payload } = action as Action<number | string>;
      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const newState = getDefaultBundleAlertDefinitionState();
      newState.filter = payload;
      newState.isLoading = true;
      return newState;
    }
    case LOAD_BUNDLE_ALERT_DEFINITIONS_FAILURE: {
      const { meta, payload } = action as ActionMeta<SimManagementError, number | string>;
      if (!meta) {
        log.warn('No meta');
        return state;
      }

      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const newState = getDefaultBundleAlertDefinitionState();
      newState.filter = meta;
      newState.error = payload;
      newState.isLoading = true;
      return newState;
    }

    case LOAD_BUNDLE_ALERT_DEFINITIONS_SUCCESS: {
      const { meta, payload } = action as ActionMeta<BundleAlertDefinition[], number | string>;
      if (!meta) {
        log.warn('No meta');
        return state;
      }

      if (!payload) {
        log.warn('No payload');
        return state;
      }

      const newState = getDefaultBundleAlertDefinitionState();
      newState.filter = meta;
      newState.all = [...payload];
      newState.byId = Object.assign({}, ...newState.all.map((a: any) => ({
        [a.id]: a
      })));
      newState.byType = Object.assign({}, ...newState.all.map((a: any) => ({
        [a.alertType]: a
      })));
      newState.expiresOn = moment().add(BundleAlertDefinitionsCacheTimeout, 'seconds').toDate();
      return newState;
    }
    case CREATE_BUNDLE_ALERT_DEFINITION_SUCCESS: {
      const typedAction = action as Action<BundleAlertDefinition>;
      const definition = typedAction.payload;
      if (definition) {
        const all: BundleAlertDefinition[] = [definition, ...state.all];
        const newState: Partial<BundleAlertDefinitionsState> = {
          all,
          byId: Object.assign({}, ...all.map((a) => ({ [a.id]: a }))),
          byType: Object.assign({}, ...all.map((a) => ({ [a.alertType]: a }))),
        };

        return Object.assign({}, state, newState);
      }

      return state;
    }
    case UPDATE_BUNDLE_ALERT_DEFINITION_SUCCESS: {
      const typedAction = action as Action<BundleAlertDefinition>;
      const definition = typedAction.payload;
      if (definition) {
        const existing = state.byId[definition.id];
        if (existing) {
          if (state.all.filter((l: any) => l.id === definition.id).length) {
            const all = [definition, ...state.all.filter((l: any) => l.id !== definition.id)];
            const newState: Partial<BundleAlertDefinitionsState> = {
              all,
              byId: Object.assign({}, ...all.map((a) => ({ [a.id]: a }))),
              byType: Object.assign({}, ...all.map((a) => ({ [a.alertType]: a }))),
            };

            return Object.assign({}, state, newState);
          }
        }
      }

      return state;
    }
    case DELETE_BUNDLE_ALERT_DEFINITION_SUCCESS: {
      const typedAction = action as Action<BundleAlertDefinition>;
      const definition = typedAction.payload;
      if (definition) {
        const all = [...state.all.filter((l: any) => l.id !== definition.id)];
        const newState: Partial<BundleAlertDefinitionsState> = {
          all,
          byId: Object.assign({}, ...all.map((a) => ({ [a.id]: a }))),
          byType: Object.assign({}, ...all.map((a) => ({ [a.alertType]: a }))),
        };

        return Object.assign({}, state, newState);
      }
    }
    default:
      return state;
  }
}
